import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import { sensorService } from '../services/sensorService';
import { locationService } from '../services/locationService';
import { thresholdService } from '../services/thresholdService';
import { exportService } from '../services/exportService';
import { growerService } from '../services/growerService';
import Layout from '../components/Layout';
import ReadingsChart from '../components/ReadingsChart';
import HistoricalReadingsModal from '../components/HistoricalReadingsModal';
import { Sensor, User, SensorReading, Threshold, Alert, ThresholdTypeValue, Location, Grower } from '../types';
import { getStoredTempUnit, convertTemperature, tempUnitLabel } from '../utils/temperatureUtils';
import { computeFillPct, computeWaterLevel } from '../utils/dds45Utils';
import { TEMP_THRESHOLD_TYPES, buildThresholdLabels } from '../utils/thresholdUtils';

const SensorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [sensor, setSensor] = useState<Sensor | null>(null);
  const [sensorLocation, setSensorLocation] = useState<Location | null>(null);
  const [latestReading, setLatestReading] = useState<SensorReading | null>(null);
  const [thresholds, setThresholds] = useState<Threshold[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [editingThreshold, setEditingThreshold] = useState<Threshold | null>(null);
  const [thresholdFormData, setThresholdFormData] = useState({
    name: '',
    threshold_type: 'temperature' as ThresholdTypeValue,
    condition: 'above' as 'above' | 'below' | 'between',
    min_value: '',
    max_value: '',
  });
  const [error, setError] = useState('');
  const [isHistoricalModalOpen, setIsHistoricalModalOpen] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showThresholds, setShowThresholds] = useState(false);
  
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDateRange, setExportDateRange] = useState('7d');
  const [exportCustomStartDate, setExportCustomStartDate] = useState('');
  const [exportCustomEndDate, setExportCustomEndDate] = useState('');
  const [exportSelectedFields, setExportSelectedFields] = useState<string[]>(['timestamp']);
  const [exportLoading, setExportLoading] = useState(false);

  // Edit sensor modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    location: '',
    location_id: null as number | null,
    sensor_type: 'LBSE-01',
    par_multiplier: '0.45',
    grower_id: null as number | null,
    is_active: true,
    tank_height_mm: '' as string,
    sensor_height_mm: '' as string,
    max_fill_height_mm: '' as string,
  });
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [growers, setGrowers] = useState<Grower[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);

  // Determine available fields based on sensor's latest reading
  const availableExportFields = React.useMemo(() => {
    const fields: Array<{ value: string; label: string; required?: boolean }> = [
      { value: 'timestamp', label: 'Timestamp', required: true },
    ];

    if (!latestReading) {
      // If no reading yet, show all possible fields
      return [
        ...fields,
        { value: 'temperature', label: 'Temperature (°C)' },
        { value: 'electrical_conductivity', label: 'EC (mS/cm)' },
        { value: 'soil_moisture', label: 'Soil Moisture (%)' },
        { value: 'battery_voltage', label: 'Battery Voltage (V)' },
        { value: 'leaf_temperature', label: 'Leaf Temperature (°C)' },
        { value: 'leaf_moisture', label: 'Leaf Moisture (%)' },
        { value: 'par', label: 'PAR (µmol/m²/s)' },
      ];
    }

    // Add fields that have data in the latest reading
    if (latestReading.temperature !== null && latestReading.temperature !== undefined) {
      fields.push({ value: 'temperature', label: 'Temperature (°C)' });
    }
    if (latestReading.electrical_conductivity !== null && latestReading.electrical_conductivity !== undefined) {
      fields.push({ value: 'electrical_conductivity', label: 'EC (mS/cm)' });
    }
    if (latestReading.soil_moisture !== null && latestReading.soil_moisture !== undefined) {
      fields.push({ value: 'soil_moisture', label: 'Soil Moisture (%)' });
    }
    if (latestReading.battery_voltage !== null && latestReading.battery_voltage !== undefined) {
      fields.push({ value: 'battery_voltage', label: 'Battery Voltage (V)' });
    }
    if (latestReading.leaf_temperature !== null && latestReading.leaf_temperature !== undefined) {
      fields.push({ value: 'leaf_temperature', label: 'Leaf Temperature (°C)' });
    }
    if (latestReading.leaf_moisture !== null && latestReading.leaf_moisture !== undefined) {
      fields.push({ value: 'leaf_moisture', label: 'Leaf Moisture (%)' });
    }

    // SE0X-LB multi-probe fields
    for (let p = 1; p <= 4; p++) {
      const tempKey = `temp_soil${p}` as keyof typeof latestReading;
      const waterKey = `water_soil${p}` as keyof typeof latestReading;
      const conductKey = `conduct_soil${p}` as keyof typeof latestReading;
      if (latestReading[tempKey] != null) {
        fields.push({ value: `temp_soil${p}`, label: `Probe ${p} Temperature (°C)` });
      }
      if (latestReading[waterKey] != null) {
        fields.push({ value: `water_soil${p}`, label: `Probe ${p} Moisture (%)` });
      }
      if (latestReading[conductKey] != null) {
        fields.push({ value: `conduct_soil${p}`, label: `Probe ${p} EC (mS/cm)` });
      }
    }

    // DDS45-LB distance field
    if (latestReading.distance_mm != null) {
      fields.push({ value: 'distance_mm', label: 'Distance (mm)' });
    }
    if (latestReading.ph_value != null) {
      fields.push({ value: 'ph_value', label: 'pH' });
    }
    if (latestReading.ph_temperature != null) {
      fields.push({ value: 'ph_temperature', label: 'pH Probe Temperature (°C)' });
    }
    if (latestReading.temp_ds18b20 != null) {
      fields.push({ value: 'temp_ds18b20', label: 'External DS18B20 Temperature (°C)' });
    }
    if (latestReading.par != null) {
      fields.push({ value: 'par', label: 'PAR (µmol/m²/s)' });
    }

    return fields;
  }, [latestReading]);

  // Threshold types available for the current sensor type, with correct temp unit label
  const availableThresholdTypes = useMemo(() => {
    const tUnit = getStoredTempUnit();
    const tLabel = tempUnitLabel(tUnit);
    const sType = sensor?.sensor_type ?? '';

    if (sType === 'LMS01') {
      return [
        { value: 'leaf_temperature', label: `${t('dashboard.leafTemperature')} (${tLabel})` },
        { value: 'leaf_moisture',    label: `${t('dashboard.leafMoisture')} (%)` },
      ];
    }

    if (sType.includes('DDS45')) {
      return [
        { value: 'distance_mm', label: `${t('sensorDetail.distance')} (mm)` },
      ];
    }

    if (sType.includes('LHT65')) {
      return [
        { value: 'air_temperature', label: `${t('lht65n.airTemperature')} (${tLabel})` },
        { value: 'air_humidity',    label: `${t('lht65n.humidity')} (%)` },
      ];
    }

    if (sType.includes('DR-ECK1.0') || latestReading?.ec_temperature != null) {
      return [
        { value: 'electrical_conductivity', label: t('wqsEc.valueAxis') },
        { value: 'ec_temperature', label: `${t('wqsEc.probeTemperature')} (${tLabel})` },
      ];
    }

    if (sType.includes('DR-PH01') || latestReading?.ph_value != null || latestReading?.ph_temperature != null) {
      return [
        { value: 'ph_value',       label: t('wqsPh.ph') },
        { value: 'ph_temperature', label: `${t('wqsPh.probeTemperature')} (${tLabel})` },
      ];
    }

    if (sType.includes('WSC2') || sType.includes('PAR') || latestReading?.par != null) {
      return [
        { value: 'par', label: t('par.unitLabel') },
      ];
    }

    if (sType === 'SE0X-LB') {
      const types: { value: string; label: string }[] = [];
      const hasReading = latestReading !== null;
      for (let p = 1; p <= 4; p++) {
        const tempKey  = `temp_soil${p}`    as keyof SensorReading;
        const waterKey = `water_soil${p}`   as keyof SensorReading;
        const condKey  = `conduct_soil${p}` as keyof SensorReading;
        const probeActive = !hasReading
          || latestReading![tempKey]  != null
          || latestReading![waterKey] != null
          || latestReading![condKey]  != null;
        if (probeActive) {
          types.push({ value: `temp_soil${p}`,    label: `${t('sensorDetail.probe')} ${p} ${t('dashboard.temperature')} (${tLabel})` });
          types.push({ value: `water_soil${p}`,   label: `${t('sensorDetail.probe')} ${p} ${t('sensorDetail.moisture')} (%)` });
          types.push({ value: `conduct_soil${p}`, label: `${t('sensorDetail.probe')} ${p} EC (mS/cm)` });
        }
      }
      return types;
    }

    // LBSE-01, SE01-LB, and any unknown soil sensor
    return [
      { value: 'temperature',             label: `${t('dashboard.temperature')} (${tLabel})` },
      { value: 'electrical_conductivity', label: 'EC (mS/cm)' },
      { value: 'soil_moisture',           label: `${t('sensorDetail.soilMoisture')} (%)` },
    ];
  }, [sensor, latestReading, t]);

  // Ref for polling interval
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingInProgressRef = useRef<boolean>(false);

  useEffect(() => {
    if (id) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Set up polling for real-time latest reading updates
  useEffect(() => {
    if (id) {
      pollingIntervalRef.current = setInterval(async () => {
        // Prevent overlapping requests
        if (isPollingInProgressRef.current) {
          return;
        }
        
        isPollingInProgressRef.current = true;
        try {
          const reading = await sensorService.getLatestReading(parseInt(id));
          setLatestReading(prev => {
            if (!prev || prev.id !== reading.id) {
              return reading;
            }
            return prev;
          });
        } catch (err) {
          // Silently fail on polling errors
        } finally {
          isPollingInProgressRef.current = false;
        }
      }, 30000); // Poll every 30 seconds
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [id]);

  const loadData = async () => {
    if (!id) return;

    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);

      const sensorData = await sensorService.getSensor(parseInt(id));
      setSensor(sensorData);

      // Load structured location if assigned
      if (sensorData.location_id) {
        try {
          const loc = await locationService.getLocation(sensorData.location_id);
          setSensorLocation(loc);
        } catch (err) {
          setSensorLocation(null);
        }
      } else {
        setSensorLocation(null);
      }

      try {
        const reading = await sensorService.getLatestReading(parseInt(id));
        setLatestReading(reading);
      } catch (err) {
        console.log('No readings available yet');
      }

      const [thresholdsData, alertsData] = await Promise.all([
        thresholdService.getThresholds(parseInt(id)),
        thresholdService.getAlerts(parseInt(id), false),
      ]);

      setThresholds(thresholdsData);
      setAlerts(alertsData);
    } catch (err) {
      console.error('Failed to load sensor data:', err);
      setError('Failed to load sensor data');
    } finally {
      setLoading(false);
    }
  };

  // Handler for when new reading is detected by the chart
  const handleNewReading = useCallback((reading: SensorReading) => {
    setLatestReading(reading);
  }, []);

  const handleOpenEditModal = async () => {
    if (!sensor) return;
    setEditFormData({
      name: sensor.name,
      description: sensor.description || '',
      location: sensor.location || '',
      location_id: sensor.location_id ?? null,
      sensor_type: sensor.sensor_type,
      par_multiplier: String(sensor.par_multiplier ?? 0.45),
      grower_id: sensor.grower_id,
      is_active: sensor.is_active,
      tank_height_mm: sensor.tank_height_mm != null ? String(sensor.tank_height_mm) : '',
      sensor_height_mm: sensor.sensor_height_mm != null ? String(sensor.sensor_height_mm) : '',
      max_fill_height_mm: sensor.max_fill_height_mm != null ? String(sensor.max_fill_height_mm) : '',
    });
    setEditError('');
    try {
      const [growersData, locationsData] = await Promise.all([
        growerService.getGrowers(),
        locationService.getLocations(),
      ]);
      setGrowers(growersData);
      setLocations(locationsData);
    } catch (_) { /* non-critical */ }
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditError('');
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sensor) return;
    setEditError('');
    setEditLoading(true);
    const isDDS45Edit = editFormData.sensor_type.includes('DDS45');
    try {
      await sensorService.updateSensor(sensor.id, {
        name: editFormData.name,
        description: editFormData.description,
        location: editFormData.location,
        sensor_type: editFormData.sensor_type,
        ...(editFormData.sensor_type === 'WSC2-L + PAR' ? {
          par_multiplier: Number(editFormData.par_multiplier),
        } : {}),
        grower_id: editFormData.grower_id,
        is_active: editFormData.is_active,
        ...(isDDS45Edit ? {
          tank_height_mm: editFormData.tank_height_mm !== '' ? parseFloat(editFormData.tank_height_mm) : null,
          sensor_height_mm: editFormData.sensor_height_mm !== '' ? parseFloat(editFormData.sensor_height_mm) : null,
          max_fill_height_mm: editFormData.max_fill_height_mm !== '' ? parseFloat(editFormData.max_fill_height_mm) : null,
        } : {}),
      });
      await locationService.assignSensorLocation(sensor.id, { location_id: editFormData.location_id });
      setShowEditModal(false);
      await loadData();
    } catch (err: any) {
      setEditError(err.response?.data?.detail || t('sensors.errorSavingSensor'));
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenThresholdModal = (threshold?: Threshold) => {
    if (threshold) {
      setEditingThreshold(threshold);
      setThresholdFormData({
        name: threshold.name,
        threshold_type: threshold.threshold_type,
        condition: threshold.condition,
        min_value: threshold.min_value?.toString() || '',
        max_value: threshold.max_value?.toString() || '',
      });
    } else {
      setEditingThreshold(null);
      // Pick a sensible default for the current sensor type
      const defaultType = (availableThresholdTypes[0]?.value ?? 'temperature') as ThresholdTypeValue;
      setThresholdFormData({
        name: '',
        threshold_type: defaultType,
        condition: 'above',
        min_value: '',
        max_value: '',
      });
    }
    setShowThresholdModal(true);
    setError('');
  };

  const handleCloseThresholdModal = () => {
    setShowThresholdModal(false);
    setEditingThreshold(null);
    setError('');
  };

  const handleThresholdSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setError('');

    const data: {
      name: string;
      threshold_type: ThresholdTypeValue;
      condition: 'above' | 'below' | 'between';
      min_value?: number;
      max_value?: number;
    } = {
      name: thresholdFormData.name,
      threshold_type: thresholdFormData.threshold_type,
      condition: thresholdFormData.condition,
    };

    if (thresholdFormData.condition === 'above' || thresholdFormData.condition === 'between') {
      data.min_value = parseFloat(thresholdFormData.min_value);
    }

    if (thresholdFormData.condition === 'below' || thresholdFormData.condition === 'between') {
      data.max_value = parseFloat(thresholdFormData.max_value);
    }

    try {
      if (editingThreshold) {
        await thresholdService.updateThreshold(editingThreshold.id, data);
      } else {
        await thresholdService.createThreshold({
          ...data,
          sensor_id: parseInt(id),
        });
      }
      handleCloseThresholdModal();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save threshold');
    }
  };

  const handleDeleteThreshold = async (thresholdId: number) => {
    if (!window.confirm(t('sensorDetail.confirmDeleteThreshold'))) {
      return;
    }

    try {
      await thresholdService.deleteThreshold(thresholdId);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete threshold');
    }
  };

  const handleResolveAlert = async (alertId: number) => {
    try {
      await thresholdService.resolveAlert(alertId);
      // Immediately remove alert from state for instant UI update
      setAlerts(prevAlerts => prevAlerts.filter(alert => alert.id !== alertId));
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to resolve alert');
    }
  };

  // Export handlers
  const handleOpenExportModal = () => {
    // Pre-select all available fields
    const defaultFields = availableExportFields.map(f => f.value);
    setExportSelectedFields(defaultFields);
    setShowExportModal(true);
  };

  const handleCloseExportModal = () => {
    setShowExportModal(false);
    setExportDateRange('7d');
    setExportCustomStartDate('');
    setExportCustomEndDate('');
  };

  const toggleExportField = (field: string) => {
    setExportSelectedFields(prev => {
      if (prev.includes(field)) {
        // Don't allow removing timestamp
        if (field === 'timestamp') return prev;
        return prev.filter(f => f !== field);
      } else {
        return [...prev, field];
      }
    });
  };

  const handleExport = async () => {
    if (!id) return;

    setExportLoading(true);
    try {
      const now = new Date();
      let startTime: string | undefined;
      let endTime: string | undefined = now.toISOString();

      if (exportDateRange === 'custom') {
        if (!exportCustomStartDate || !exportCustomEndDate) {
          alert(t('sensorDetail.selectDatesAlert'));
          setExportLoading(false);
          return;
        }
        startTime = new Date(exportCustomStartDate).toISOString();
        endTime = new Date(exportCustomEndDate).toISOString();
      } else {
        // Calculate start time based on preset
        const start = new Date();
        switch (exportDateRange) {
          case '1d':
            start.setDate(start.getDate() - 1);
            break;
          case '7d':
            start.setDate(start.getDate() - 7);
            break;
          case '30d':
            start.setDate(start.getDate() - 30);
            break;
          case '90d':
            start.setDate(start.getDate() - 90);
            break;
        }
        startTime = start.toISOString();
      }

      await exportService.exportSensorReadings({
        sensorId: parseInt(id),
        startTime,
        endTime,
        fields: exportSelectedFields,
      });

      handleCloseExportModal();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const _threshTempUnit = getStoredTempUnit();

  const THRESHOLD_LABELS: Record<string, string> = buildThresholdLabels(_threshTempUnit);

  const getThresholdLabel = (type: string) => {
    return THRESHOLD_LABELS[type] || type;
  };

  const getConditionText = (threshold: Threshold) => {
    const { condition, min_value, max_value, threshold_type } = threshold;
    const isTemp = TEMP_THRESHOLD_TYPES.has(threshold_type);
    const fmt = (v: number | null) => {
      if (v === null) return '';
      const converted = isTemp ? convertTemperature(v, _threshTempUnit) : v;
      return converted != null ? converted.toFixed(1) : '';
    };
    if (condition === 'above') return `> ${fmt(min_value)}`;
    if (condition === 'below') return `< ${fmt(max_value)}`;
    return `${fmt(min_value)} - ${fmt(max_value)}`;
  };

  if (loading) {
    return (
      <Layout user={user || undefined}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">{t('common.loading')}</div>
        </div>
      </Layout>
    );
  }

  if (!sensor) {
    return (
      <Layout user={user || undefined}>
        <div className="text-center py-12">
          <p className="text-gray-500">{t('sensorDetail.sensorNotFound')}</p>
          <button
            onClick={() => navigate('/sensors')}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            {t('sensorDetail.backToSensors')}
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user || undefined}>
      <div className="py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <button
              onClick={() => navigate('/sensors')}
              className="text-sm text-primary-600 hover:text-primary-700 mb-2"
            >
              {t('sensorDetail.backToSensors')}
            </button>
            <h2 className="text-2xl font-semibold text-gray-900">{sensor.name}</h2>
            <p className="text-sm text-gray-500">{t('sensorDetail.deviceId')} {sensor.device_id}</p>
            {user?.is_admin && (
              <p className="text-sm text-gray-500 mt-1">
                {t('sensors.grower')}:{' '}
                <span className={sensor.grower_id ? 'font-medium text-gray-700' : 'text-amber-700 italic'}>
                  {sensor.grower_id
                    ? (sensor.grower_name || t('common.unknown'))
                    : t('sensors.unassigned')}
                </span>
              </p>
            )}
            {user?.is_admin && (
              <p className="text-sm text-gray-500 mt-1">
                {t('sensors.gateway')}:{' '}
                {sensor.last_seen_gateway_id ? (
                  <Link
                    to={`/gateways/${encodeURIComponent(sensor.last_seen_gateway_id)}`}
                    className="text-xs font-medium text-primary-600 hover:text-primary-800 break-all"
                    title={sensor.last_seen_gateway_id}
                  >
                    {sensor.last_seen_gateway_internal_name || sensor.last_seen_gateway_id}
                  </Link>
                ) : (
                  <span className="text-gray-400">{t('sensors.noGateway')}</span>
                )}
              </p>
            )}
            {sensorLocation ? (
              <p className="text-sm text-gray-500 mt-1">
                📍 {t('sensors.location')}:{' '}
                <Link to="/locations" className="text-primary-600 hover:text-primary-700 font-medium">
                  {sensorLocation.name}
                </Link>
              </p>
            ) : sensor.location ? (
              <p className="text-sm text-gray-500 mt-1">📍 {t('sensors.location')}: {sensor.location}</p>
            ) : null}
          </div>
          <div className="flex items-center space-x-3">
            {(user?.is_admin || user?.is_admin_grower) && (
              <button
                onClick={handleOpenEditModal}
                className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                ✏️ {t('sensors.editSensor')}
              </button>
            )}
            {sensor.tti_registered && (
              <button
                onClick={() => setIsHistoricalModalOpen(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                📥 {t('sensorDetail.manageReadings')}
              </button>
            )}
            <button
              onClick={handleOpenExportModal}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              📊 {t('export.exportToExcel')}
            </button>
            <span
              className={`px-3 py-1 text-sm rounded-full ${
                sensor.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {sensor.is_active ? t('common.active') : t('common.inactive')}
            </span>
          </div>
        </div>

        {/* Alert and Threshold Management Bar */}
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => handleOpenThresholdModal()}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              {t('sensorDetail.addThreshold')}
            </button>
            <button
              onClick={() => setShowThresholds(!showThresholds)}
              className="flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 transition-colors"
            >
              <span className="font-medium">{t('sensorDetail.thresholds')}</span>
              <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white rounded-full text-sm font-semibold">
                {thresholds.length}
              </span>
            </button>
            <button
              onClick={() => setShowAlerts(!showAlerts)}
              className={`flex items-center px-4 py-2 rounded-md transition-colors ${
                alerts.length > 0
                  ? 'bg-red-100 text-red-800 hover:bg-red-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className="font-medium">{t('sensorDetail.activeAlerts')}</span>
              <span className={`ml-2 px-2 py-0.5 rounded-full text-sm font-semibold ${
                alerts.length > 0 ? 'bg-red-600 text-white' : 'bg-gray-400 text-white'
              }`}>
                {alerts.length}
              </span>
            </button>
          </div>
        </div>

        {/* Alerts Panel (Collapsible) */}
        {showAlerts && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('sensorDetail.activeAlerts')}</h3>
            {alerts.length === 0 ? (
              <p className="text-gray-500">{t('sensorDetail.noActiveAlerts')}</p>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className="flex justify-between items-center p-4 bg-red-50 border border-red-200 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-red-800">{alert.message}</p>
                      <p className="text-xs text-red-600 mt-1">
                        {t('sensorDetail.alertValue')} {alert.value} | {new Date(alert.created_at).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => handleResolveAlert(alert.id)}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      {t('sensorDetail.resolve')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Thresholds Panel (Collapsible) */}
        {showThresholds && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('sensorDetail.thresholds')}</h3>
            {thresholds.length === 0 ? (
              <p className="text-gray-500">{t('sensorDetail.noThresholds')}</p>
            ) : (
              <div className="space-y-3">
                {thresholds.map((threshold) => (
                  <div
                    key={threshold.id}
                    className="flex justify-between items-center p-4 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{threshold.name}</p>
                      <p className="text-xs text-gray-600 mt-1">
                        {getThresholdLabel(threshold.threshold_type)} {getConditionText(threshold)}
                      </p>
                      <span
                        className={`inline-block mt-2 px-2 py-1 text-xs rounded-full ${
                          threshold.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {threshold.is_active ? t('common.active') : t('common.inactive')}
                      </span>
                      {threshold.created_by && (
                        <p className="text-xs text-gray-400 mt-1">
                          {t('sensorDetail.createdBy')}: {threshold.created_by.full_name || threshold.created_by.email}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        {t('sensorDetail.createdAt')}: {new Date(threshold.created_at).toLocaleString()}
                      </p>
                      {threshold.updated_at && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {t('sensorDetail.lastEdited')}: {new Date(threshold.updated_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleOpenThresholdModal(threshold)}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteThreshold(threshold.id)}
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Latest Reading */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('dashboard.latestReading')}</h3>
          {latestReading ? (
            (() => {
              const isLeafSensor = latestReading.leaf_moisture !== null || latestReading.leaf_temperature !== null;
              const isDDS45 = latestReading.distance_mm != null || (sensor?.sensor_type ?? '').includes('DDS45');
              const isLHT65N = latestReading.air_temperature != null || latestReading.air_humidity != null || (sensor?.sensor_type ?? '').includes('LHT65');
              const isWQSEC = latestReading.ec_temperature != null || (sensor?.sensor_type ?? '').includes('DR-ECK1.0');
              const isWQSPH = latestReading.ph_value != null || latestReading.ph_temperature != null || (sensor?.sensor_type ?? '').includes('DR-PH01');
              const isPAR = latestReading.par != null || (sensor?.sensor_type ?? '').includes('WSC2') || (sensor?.sensor_type ?? '').includes('PAR');
              const isSE0X = (
                latestReading.temp_soil1 != null || latestReading.water_soil1 != null || latestReading.conduct_soil1 != null ||
                latestReading.temp_soil2 != null || latestReading.water_soil2 != null || latestReading.conduct_soil2 != null ||
                latestReading.temp_soil3 != null || latestReading.water_soil3 != null || latestReading.conduct_soil3 != null ||
                latestReading.temp_soil4 != null || latestReading.water_soil4 != null || latestReading.conduct_soil4 != null ||
                latestReading.probe_flags != null
              );
              
              if (isPAR) {
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded">{t('par.sensorLabel')}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {latestReading.par != null && (
                        <div className="bg-emerald-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('par.par')}</p>
                          <p className="text-2xl font-bold text-emerald-700">{latestReading.par.toFixed(0)} µmol/m²/s</p>
                        </div>
                      )}
                      {latestReading.battery_voltage != null && (
                        <div className="bg-yellow-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('dashboard.battery')}</p>
                          <p className="text-2xl font-bold text-yellow-600">{latestReading.battery_voltage.toFixed(2)} V</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-400">
                      {t('sensorDetail.lastUpdated')} {new Date(latestReading.timestamp).toLocaleString()}
                    </div>
                  </div>
                );
              } else if (isWQSEC) {
                const tUnit = getStoredTempUnit();
                const displayProbeTemp = latestReading.ec_temperature != null
                  ? convertTemperature(latestReading.ec_temperature, tUnit)
                  : null;
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm font-medium text-cyan-700 bg-cyan-50 px-2.5 py-1 rounded">{t('wqsEc.sensorLabel')}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {latestReading.electrical_conductivity != null && (
                        <div className="bg-cyan-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('wqsEc.ec')}</p>
                          <p className="text-2xl font-bold text-cyan-700">{latestReading.electrical_conductivity.toFixed(0)} µS/cm</p>
                        </div>
                      )}
                      {displayProbeTemp != null && (
                        <div className="bg-red-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('wqsEc.probeTemperature')}</p>
                          <p className="text-2xl font-bold text-red-600">{displayProbeTemp.toFixed(1)} {tempUnitLabel(tUnit)}</p>
                        </div>
                      )}
                      {latestReading.battery_voltage != null && (
                        <div className="bg-yellow-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('dashboard.battery')}</p>
                          <p className="text-2xl font-bold text-yellow-600">{latestReading.battery_voltage.toFixed(2)} V</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-400">
                      {t('sensorDetail.lastUpdated')} {new Date(latestReading.timestamp).toLocaleString()}
                    </div>
                  </div>
                );
              } else if (isWQSPH) {
                const tUnit = getStoredTempUnit();
                const displayTemp = latestReading.temp_ds18b20 != null
                  ? convertTemperature(latestReading.temp_ds18b20, tUnit)
                  : null;
                const displayProbeTemp = latestReading.ph_temperature != null
                  ? convertTemperature(latestReading.ph_temperature, tUnit)
                  : null;
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm font-medium text-cyan-700 bg-cyan-50 px-2.5 py-1 rounded">{t('wqsPh.sensorLabel')}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {latestReading.ph_value != null && (
                        <div className="bg-cyan-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('wqsPh.ph')}</p>
                          <p className="text-2xl font-bold text-cyan-700">{latestReading.ph_value.toFixed(2)}</p>
                        </div>
                      )}
                      {displayProbeTemp != null && (
                        <div className="bg-red-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('wqsPh.probeTemperature')}</p>
                          <p className="text-2xl font-bold text-red-600">{displayProbeTemp.toFixed(1)} {tempUnitLabel(tUnit)}</p>
                        </div>
                      )}
                      {displayTemp != null && (
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('wqsPh.waterTemperature')}</p>
                          <p className="text-2xl font-bold text-purple-600">{displayTemp.toFixed(1)} {tempUnitLabel(tUnit)}</p>
                        </div>
                      )}
                      {latestReading.battery_voltage != null && (
                        <div className="bg-yellow-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('dashboard.battery')}</p>
                          <p className="text-2xl font-bold text-yellow-600">{latestReading.battery_voltage.toFixed(2)} V</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-400">
                      {t('sensorDetail.lastUpdated')} {new Date(latestReading.timestamp).toLocaleString()}
                    </div>
                  </div>
                );
              } else if (isDDS45) {
                // Display DDS45-LB Distance/Level Sensor data
                const waterLevelMm = computeWaterLevel(
                  latestReading.distance_mm,
                  sensor?.sensor_height_mm
                );
                const fillPct = computeFillPct(
                  latestReading.distance_mm,
                  sensor?.sensor_height_mm,
                  sensor?.max_fill_height_mm
                );
                const hasSensorHeight = sensor?.sensor_height_mm != null;
                const hasTankCfg = hasSensorHeight && sensor?.max_fill_height_mm != null;
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm font-medium text-blue-700 bg-blue-50 px-2.5 py-1 rounded">{t('sensorDetail.dds45Sensor')}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {user?.is_admin && latestReading.distance_mm != null && (
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('sensorDetail.distance')}</p>
                          <p className="text-2xl font-bold text-blue-600">{latestReading.distance_mm} mm</p>
                        </div>
                      )}
                      {waterLevelMm !== null && (
                        <div className="bg-cyan-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('sensorDetail.waterLevel')}</p>
                          <p className="text-2xl font-bold text-cyan-600">{(waterLevelMm / 10).toFixed(1)} cm</p>
                        </div>
                      )}
                      {fillPct !== null && (
                        <div className="bg-green-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('sensorDetail.fillLevel')}</p>
                          <p className="text-2xl font-bold text-green-600">{fillPct.toFixed(1)}%</p>
                        </div>
                      )}
                    </div>
                    {!hasSensorHeight && (
                      <p className="mt-3 text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
                        {t('sensorDetail.tankConfigMissing')}
                      </p>
                    )}
                    {hasSensorHeight && !hasTankCfg && (
                      <p className="mt-3 text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
                        {t('sensorDetail.maxFillHeightMissing')}
                      </p>
                    )}
                    <div className="mt-2 text-xs text-gray-400">
                      {t('sensorDetail.lastUpdated')} {new Date(latestReading.timestamp).toLocaleString()}
                    </div>
                  </div>
                );
              } else if (isLHT65N) {
                // Display LHT65N/S Temperature & Humidity Sensor data
                const tUnit = getStoredTempUnit();
                const tLabel = tempUnitLabel(tUnit);
                const displayTemp = latestReading.air_temperature != null
                  ? convertTemperature(latestReading.air_temperature, tUnit)
                  : null;
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm font-medium text-red-700 bg-red-50 px-2.5 py-1 rounded">{t('lht65n.sensorLabel')}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {displayTemp !== null && (
                        <div className="bg-red-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('lht65n.airTemperature')}</p>
                          <p className="text-2xl font-bold text-red-600">{displayTemp.toFixed(1)} {tLabel}</p>
                        </div>
                      )}
                      {latestReading.air_humidity != null && (
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('lht65n.humidity')}</p>
                          <p className="text-2xl font-bold text-blue-600">{latestReading.air_humidity.toFixed(1)}%</p>
                        </div>
                      )}
                      {latestReading.battery_voltage != null && (
                        <div className="bg-yellow-50 p-4 rounded-lg">
                          <p className="text-sm text-gray-600">{t('dashboard.battery')}</p>
                          <p className="text-2xl font-bold text-yellow-600">{latestReading.battery_voltage.toFixed(2)} V</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-400">
                      {t('sensorDetail.lastUpdated')} {new Date(latestReading.timestamp).toLocaleString()}
                    </div>
                  </div>
                );
              } else if (isSE0X) {
                // Detect active probes
                const activeProbes: number[] = [];
                if (latestReading.temp_soil1 != null || latestReading.water_soil1 != null || latestReading.conduct_soil1 != null) activeProbes.push(1);
                if (latestReading.temp_soil2 != null || latestReading.water_soil2 != null || latestReading.conduct_soil2 != null) activeProbes.push(2);
                if (latestReading.temp_soil3 != null || latestReading.water_soil3 != null || latestReading.conduct_soil3 != null) activeProbes.push(3);
                if (latestReading.temp_soil4 != null || latestReading.water_soil4 != null || latestReading.conduct_soil4 != null) activeProbes.push(4);
                
                const getProbeData = (p: number) => {
                  switch (p) {
                    case 1: return { temp: latestReading.temp_soil1, moisture: latestReading.water_soil1, ec: latestReading.conduct_soil1 };
                    case 2: return { temp: latestReading.temp_soil2, moisture: latestReading.water_soil2, ec: latestReading.conduct_soil2 };
                    case 3: return { temp: latestReading.temp_soil3, moisture: latestReading.water_soil3, ec: latestReading.conduct_soil3 };
                    case 4: return { temp: latestReading.temp_soil4, moisture: latestReading.water_soil4, ec: latestReading.conduct_soil4 };
                    default: return { temp: null, moisture: null, ec: null };
                  }
                };
                
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-sm font-medium text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded">{t('sensorDetail.se0xSensor')}</span>
                      <span className="text-sm text-gray-500">{t('sensorDetail.probeActive', { count: activeProbes.length })}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {activeProbes.map(p => {
                        const data = getProbeData(p);
                        const _tempUnit = getStoredTempUnit();
                        const _unitLabel = tempUnitLabel(_tempUnit);
                        return (
                          <div key={p} className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-sm font-semibold text-gray-700 mb-3">{t('sensorDetail.probe')} {p}</p>
                            <div className="grid grid-cols-3 gap-3">
                              {data.temp != null && (
                                <div className="bg-blue-50 p-3 rounded-lg text-center">
                                  <p className="text-xs text-gray-500">{t('dashboard.temperature')}</p>
                                  <p className="text-lg font-bold text-blue-600">{(convertTemperature(Number(data.temp), _tempUnit) ?? 0).toFixed(1)}{_unitLabel}</p>
                                </div>
                              )}
                              {data.moisture != null && (
                                <div className="bg-green-50 p-3 rounded-lg text-center">
                                  <p className="text-xs text-gray-500">{t('sensorDetail.moisture')}</p>
                                  <p className="text-lg font-bold text-green-600">{Number(data.moisture).toFixed(1)}%</p>
                                </div>
                              )}
                              {data.ec != null && (
                                <div className="bg-purple-50 p-3 rounded-lg text-center">
                                  <p className="text-xs text-gray-500">{t('common.ec')}</p>
                                  <p className="text-lg font-bold text-purple-600">{(Number(data.ec) / 1000).toFixed(2)} mS/cm</p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {latestReading.battery_voltage != null && (
                      <div className="mt-3 text-sm text-gray-500">{t('sensorDetail.batteryValue')} {latestReading.battery_voltage.toFixed(2)}V</div>
                    )}
                    {latestReading.temp_ds18b20 != null && (
                      <div className="mt-1 text-sm text-gray-500">{t('sensorDetail.ds18b20Temp')} {(convertTemperature(latestReading.temp_ds18b20, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</div>
                    )}
                    <div className="mt-2 text-xs text-gray-400">
                      {t('sensorDetail.lastUpdated')} {new Date(latestReading.timestamp).toLocaleString()}
                    </div>
                  </div>
                );
              } else if (isLeafSensor) {
                // Display LMS01-LB Leaf Moisture Sensor data
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {latestReading.leaf_temperature !== null && latestReading.leaf_temperature !== undefined && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600">{t('dashboard.leafTemperature')}</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {(convertTemperature(latestReading.leaf_temperature, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}
                        </p>
                      </div>
                    )}
                    {latestReading.leaf_moisture !== null && latestReading.leaf_moisture !== undefined && (
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-600">{t('dashboard.leafMoisture')}</p>
                        <p className="text-2xl font-bold text-green-600">
                          {latestReading.leaf_moisture.toFixed(1)}%
                        </p>
                      </div>
                    )}
                    <div className="col-span-1 md:col-span-3 text-xs text-gray-400">
                      {t('sensorDetail.lastUpdated')} {new Date(latestReading.timestamp).toLocaleString()}
                    </div>
                  </div>
                );
              } else {
                // Display LBSE-01 Soil Sensor data
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">{t('dashboard.temperature')}</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {(convertTemperature(latestReading.temperature, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}
                      </p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">{t('sensorDetail.soilMoisture')}</p>
                      <p className="text-2xl font-bold text-green-600">
                        {latestReading.soil_moisture?.toFixed(1)}%
                      </p>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">{t('common.ec')}</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {latestReading.electrical_conductivity != null ? (latestReading.electrical_conductivity / 1000).toFixed(2) : '—'} mS/cm
                      </p>
                    </div>
                    <div className="col-span-1 md:col-span-3 text-xs text-gray-400">
                      {t('sensorDetail.lastUpdated')} {new Date(latestReading.timestamp).toLocaleString()}
                    </div>
                  </div>
                );
              }
            })()
          ) : (
            <p className="text-gray-500">{t('sensorDetail.noReadingsYet')}</p>
          )}
        </div>

        {/* Readings Chart */}
        <div className="mb-6">
          <ReadingsChart
            sensorId={parseInt(id!)}
            sensorType={sensor.sensor_type}
            sensor={sensor}
            latestReading={latestReading}
            onNewReading={handleNewReading}
          />
        </div>

        {/* TTI Registration Status - Only visible to admins */}
        {sensor.tti_registered && user?.is_admin && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('sensorDetail.ttiStatus')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">{t('sensorDetail.registrationStatus')}</p>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {t('sensorDetail.registered')}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('sensorDetail.cloudRegion')}</p>
                <p className="font-medium text-gray-900">{sensor.tti_region?.toUpperCase()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('sensorDetail.ttiDeviceId')}</p>
                <p className="font-medium text-gray-900">{sensor.tti_device_id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('sensorDetail.applicationId')}</p>
                <p className="font-medium text-gray-900">{sensor.tti_app_id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('sensorDetail.deviceEui')}</p>
                <p className="font-mono text-sm text-gray-900">{sensor.tti_dev_eui}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('sensorDetail.joinEui')}</p>
                <p className="font-mono text-sm text-gray-900">{sensor.tti_join_eui}</p>
              </div>
              {sensor.tti_registration_timestamp && (
                <div className="col-span-1 md:col-span-2">
                  <p className="text-xs text-gray-400">
                    {t('sensorDetail.registeredOn')} {new Date(sensor.tti_registration_timestamp).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Threshold Modal */}
        {showThresholdModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-4 sm:mx-auto p-5 border w-auto max-w-sm sm:max-w-md shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingThreshold ? t('sensorDetail.editThresholdTitle') : t('sensorDetail.addThresholdTitle')}
              </h3>
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}
              <form onSubmit={handleThresholdSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('sensorDetail.thresholdName')} *</label>
                  <input
                    type="text"
                    required
                    value={thresholdFormData.name}
                    onChange={(e) => setThresholdFormData({ ...thresholdFormData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('sensorDetail.thresholdType')} *</label>
                  <select
                    required
                    value={thresholdFormData.threshold_type}
                    onChange={(e) => setThresholdFormData({ ...thresholdFormData, threshold_type: e.target.value as ThresholdTypeValue })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    {availableThresholdTypes.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('sensorDetail.thresholdCondition')} *
                  </label>
                  <select
                    required
                    value={thresholdFormData.condition}
                    onChange={(e) => setThresholdFormData({ ...thresholdFormData, condition: e.target.value as 'above' | 'below' | 'between' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="above">{t('sensorDetail.above')}</option>
                    <option value="below">{t('sensorDetail.below')}</option>
                    <option value="between">{t('sensorDetail.between')}</option>
                  </select>
                </div>
                {(thresholdFormData.condition === 'above' || thresholdFormData.condition === 'between') && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {thresholdFormData.condition === 'above' ? t('sensorDetail.thresholdValue') : t('sensorDetail.minValue')} *
                    </label>
                    <input
                      type="number"
                      step="any"
                      required
                      value={thresholdFormData.min_value}
                      onChange={(e) => setThresholdFormData({ ...thresholdFormData, min_value: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                )}
                {(thresholdFormData.condition === 'below' || thresholdFormData.condition === 'between') && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {thresholdFormData.condition === 'below' ? t('sensorDetail.thresholdValue') : t('sensorDetail.maxValue')} *
                    </label>
                    <input
                      type="number"
                      step="any"
                      required
                      value={thresholdFormData.max_value}
                      onChange={(e) => setThresholdFormData({ ...thresholdFormData, max_value: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                )}
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={handleCloseThresholdModal}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                  >
                    {editingThreshold ? t('dashboard.update') : t('sensorDetail.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Export Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('sensorDetail.exportTitle')}
              </h3>

              {/* Date Range Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('export.dateRange')}
                </label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { value: '1d', label: t('sensorDetail.last24Hours') },
                    { value: '7d', label: t('sensorDetail.last7Days') },
                    { value: '30d', label: t('sensorDetail.last30Days') },
                    { value: '90d', label: t('sensorDetail.last90Days') },
                    { value: 'custom', label: t('sensorDetail.customRange') },
                  ].map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setExportDateRange(option.value)}
                      className={`px-4 py-2 text-sm rounded-md border transition-colors ${
                        exportDateRange === option.value
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {/* Custom Date Range Inputs */}
                {exportDateRange === 'custom' && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('sensorDetail.startDate')}
                      </label>
                      <input
                        type="datetime-local"
                        value={exportCustomStartDate}
                        onChange={(e) => setExportCustomStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t('sensorDetail.endDate')}
                      </label>
                      <input
                        type="datetime-local"
                        value={exportCustomEndDate}
                        onChange={(e) => setExportCustomEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Field Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('sensorDetail.fieldsToExport')}
                </label>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  {availableExportFields.map(field => (
                    <label
                      key={field.value}
                      className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 p-2 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={exportSelectedFields.includes(field.value)}
                        onChange={() => toggleExportField(field.value)}
                        disabled={field.required}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        {field.label}
                        {field.required && <span className="text-gray-400 ml-1">({t('sensorDetail.required')})</span>}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('sensorDetail.selectedField', { count: exportSelectedFields.length })}
                  {latestReading && ` • ${t('sensorDetail.onlyFieldsWithData')}`}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={handleCloseExportModal}
                  disabled={exportLoading}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors disabled:opacity-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={exportLoading || exportSelectedFields.length === 0}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exportLoading ? t('export.exporting') : t('export.exportToExcel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Sensor Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white mb-10">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('sensors.editSensor')}</h3>
              {editError && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                  {editError}
                </div>
              )}
              <form onSubmit={handleEditSubmit}>
                {/* Device ID (read-only) */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('sensors.deviceId')}</label>
                  <input
                    type="text"
                    disabled
                    value={sensor.device_id}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                  />
                </div>

                {/* Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.name')} *</label>
                  <input
                    type="text"
                    required
                    value={editFormData.name}
                    onChange={e => setEditFormData({ ...editFormData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Sensor Type — admin only */}
                {user?.is_admin && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('sensors.sensorType')}</label>
                    <select
                      value={editFormData.sensor_type}
                      onChange={e => setEditFormData({ ...editFormData, sensor_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="LBSE-01">LBSE-01 (Soil)</option>
                      <option value="SE01-LB">SE01-LB (Soil)</option>
                      <option value="LMS01">LMS01 (Leaf)</option>
                      <option value="SE0X-LB">SE0X-LB (Multi-probe)</option>
                      <option value="DDS45-LB">DDS45-LB (Distance/Level)</option>
                      <option value="LHT65N">LHT65N (Temperature &amp; Humidity)</option>
                      <option value="WQS-LB + DR-PH01">{t('wqsPh.sensorLabel')}</option>
                      <option value="WQS-LB + DR-ECK1.0">{t('wqsEc.sensorLabel')}</option>
                      <option value="WSC2-L + PAR">{t('par.sensorLabel')}</option>
                    </select>
                  </div>
                )}

                {/* PAR calibration — admin only */}
                {user?.is_admin && editFormData.sensor_type === 'WSC2-L + PAR' && (
                  <div className="mb-4">
                    <label htmlFor="edit-par-multiplier" className="block text-sm font-medium text-gray-700 mb-2">{t('par.multiplier')}</label>
                    <input id="edit-par-multiplier" type="number" min="0.001" step="any" required
                      value={editFormData.par_multiplier}
                      onChange={e => setEditFormData({ ...editFormData, par_multiplier: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500" />
                    <p className="text-xs text-gray-500 mt-1">{t('par.multiplierHint')}</p>
                  </div>
                )}

                {/* Assign to Grower — admin only */}
                {user?.is_admin && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('sensors.assignToGrower')}</label>
                    <select
                      value={editFormData.grower_id ?? ''}
                      onChange={e => setEditFormData({ ...editFormData, grower_id: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">{t('sensors.noGrower')} ({t('common.unassigned')})</option>
                      {growers.map(g => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Description */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.description')}</label>
                  <textarea
                    value={editFormData.description}
                    onChange={e => setEditFormData({ ...editFormData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Structured location — admin only */}
                {user?.is_admin && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('sensors.location')}</label>
                    <select
                      value={editFormData.location_id ?? ''}
                      onChange={e => setEditFormData({ ...editFormData, location_id: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">{t('sensors.noLocation')}</option>
                      {locations
                        .filter(loc => !editFormData.grower_id || loc.grower_id === editFormData.grower_id)
                        .map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                    </select>
                  </div>
                )}

                {/* Location note */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('sensors.locationNote')} <span className="text-gray-400 font-normal">({t('common.freeText')})</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData.location}
                    onChange={e => setEditFormData({ ...editFormData, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('sensors.locationPlaceholder')}
                  />
                </div>

                {/* Active status — admin only */}
                {user?.is_admin && (
                  <div className="mb-4 flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="edit-is-active"
                      checked={editFormData.is_active}
                      onChange={e => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                      className="rounded text-primary-600"
                    />
                    <label htmlFor="edit-is-active" className="text-sm font-medium text-gray-700">{t('common.active')}</label>
                  </div>
                )}

                {/* DDS45-LB Tank Configuration — admin only */}
                {user?.is_admin && editFormData.sensor_type.includes('DDS45') && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">{t('sensors.dds45Settings')}</h4>
                    <p className="text-xs text-gray-500 mb-3">{t('sensors.dds45SettingsDescription')}</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('sensors.tankHeight')} (mm)</label>
                        <input
                          type="number" min="0" step="1"
                          value={editFormData.tank_height_mm}
                          onChange={e => setEditFormData({ ...editFormData, tank_height_mm: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g. 3500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('sensors.sensorHeight')} (mm)</label>
                        <input
                          type="number" min="0" step="1"
                          value={editFormData.sensor_height_mm}
                          onChange={e => setEditFormData({ ...editFormData, sensor_height_mm: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g. 3500"
                        />
                        <p className="text-xs text-gray-400 mt-1">{t('sensors.sensorHeightHint')}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('sensors.maxFillHeight')} (mm)</label>
                        <input
                          type="number" min="0" step="1"
                          value={editFormData.max_fill_height_mm}
                          onChange={e => setEditFormData({ ...editFormData, max_fill_height_mm: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="e.g. 3000"
                        />
                        <p className="text-xs text-gray-400 mt-1">{t('sensors.maxFillHeightHint')}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex space-x-2 pt-2">
                  <button
                    type="button"
                    onClick={handleCloseEditModal}
                    disabled={editLoading}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors disabled:opacity-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {editLoading ? t('common.saving') : t('common.update')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Historical Readings Modal */}
        <HistoricalReadingsModal
          sensorId={sensor.id}
          sensorName={sensor.name}
          isOpen={isHistoricalModalOpen}
          onClose={() => setIsHistoricalModalOpen(false)}
          onSuccess={() => { loadData(); }}
        />
      </div>
    </Layout>
  );
};

export default SensorDetail;
